import { Injectable, Injector, inject } from '@angular/core';
import moment, { Moment } from 'moment';
import { Observable, forkJoin, from as from$, of } from 'rxjs';
import { catchError, map, mergeMap, toArray } from 'rxjs/operators';
import {
    FINANCE_FETCH_CONCURRENCY,
    STORE_SYNC_CONCURRENCY,
} from '../common/throttle-retry';
import { splitWindows, storeService } from '../common/store-scoped';
import { get as _get, keyBy as _keyBy } from 'lodash';

import {
    LazadaStoreConfig,
    ShopeeStoreConfig,
    StoreConfig,
    StoreId,
    STORES,
    TiktokStoreConfig,
} from '../../config/store-config';
import {
    isVatRegisteredAt,
    splitVatInclusive,
} from '../../config/entity-config';
import { LazadaOpenService } from '../lazada/lazada-open.service';
import { ShopeeOpenServiceV2 } from '../shopee/shopee-open-v2.service';
import { TiktokOpenService } from '../tiktok/tiktok-open.service';
import {
    FinanceOrderRecord,
    round2,
    vatBearingFeeTotal,
} from './finance-record';
import { normalizeShopeeOrder, reconcileShopee } from './normalize-shopee';
import { normalizeLazadaOrders } from './normalize-lazada';
import {
    normalizeTiktokTransaction,
    reconcileTiktok,
} from './normalize-tiktok';

// Pulls a period's finance data from all five stores, normalizes it, and
// returns records ready to upsert. Deliberately does NOT write — the caller
// decides whether to persist, so a sync can be inspected before it lands.

export interface FinanceSyncStoreReport {
    storeId: StoreId;
    title: string;
    records: number;
    // A store that fails does not abort the sync; its error is reported and
    // the other four still produce records. A month missing one shop is
    // obviously wrong; a month silently missing one shop is not.
    error?: string;
    // Rows whose fee taxonomy did not reproduce the platform's own settlement.
    // Non-zero means a fee was dropped, double-counted or mis-signed — the
    // figures cannot be trusted until it is explained.
    reconciliationFailures: number;
    // Lazada only: fee names FEE_NAME_BUCKET does not know. They still land in
    // `other` so no money is lost, but they need mapping.
    unmappedFeeNames?: string[];
    // Lazada only: account-level rows with no order_no.
    unattributedTotal?: number;
    // Lazada only: ledger lines whose amount would not parse and so counted
    // as zero. Money is missing from the records until it is explained.
    unparsedAmounts?: number;
    // Records with no tax point, which are therefore invisible to a
    // tax-point-basis period query.
    missingTaxPoint: number;
    // Shopee only: settled orders whose order/get_order_detail row did not
    // come back, even after a second ask, and which were therefore NOT
    // written. Writing them would upsert a null tax point over the one a
    // previous sync stored (2026-10-23: one chunk of 50 April orders came
    // back as an empty order_list with no error, and the re-sync nulled 50
    // stored tax points). The month needs a re-sync.
    unjoinedOrders?: number;
    // Two records in one sync sharing an _id — the second would overwrite the
    // first and its money would vanish without any error. Ids are per
    // settlement slice (FinanceOrderRecord.part), so a non-zero count now
    // means a platform reported one order twice inside a single statement or
    // ledger month. While ids were per order this fired 2–3 times a month on
    // TikTok: that was the cross-statement overwrite the part key removed.
    duplicateIds: number;
}

export interface FinanceSyncResult {
    records: FinanceOrderRecord[];
    reports: FinanceSyncStoreReport[];
}

// What a platform says a period contains, asked directly rather than inferred
// from anything the sync recorded. The page compares this against the stored
// totals so a truncated sync shows as a discrepancy instead of as a quiet
// month.
export interface FinanceVerifyReport {
    storeId: StoreId;
    title: string;
    // null where the platform has no per-order index at this level — TikTok
    // reports settled totals per statement, not per order. null means "not
    // available", never "zero".
    platformOrders: number | null;
    platformSettlement: number | null;
    // Set when the probe itself failed. A failed probe must never present as
    // zeroes, which would read as the platform confirming an empty month.
    error?: string;
}

// Shopee caps get_escrow_list at roughly a 15-day window, so a month is three
// calls. Split conservatively at 14 days.
const SHOPEE_WINDOW_DAYS = 14;

// How far before the reporting period to look for Lazada order-creation dates.
// An order that settles in July was created earlier — usually days, but
// disputed or slow-delivery orders can lag by weeks. 90 days is generous
// enough that a miss means something unusual, and the sync reports misses
// rather than dating them from the ledger.
const LAZADA_ORDER_LOOKBACK_DAYS = 90;

@Injectable({
    providedIn: 'root',
})
export class FinanceSyncService {
    private injector = inject(Injector);

    // `from`/`to` bound the period on each platform's own natural axis —
    // settlement for Shopee and TikTok, ledger date for Lazada — because that
    // is what the finance endpoints accept. Which date a row is finally
    // *reported* under is a read-time choice made against the stored record.
    sync(from: Moment, to: Moment): Observable<FinanceSyncResult> {
        // Stores run at STORE_SYNC_CONCURRENCY, not all at once. Running the
        // five in parallel exceeded SSM Parameter Store's 40 TPS account
        // ceiling — the delegates read credentials per invoke — and failed
        // three of five stores with `Rate exceeded`. See throttle-retry.ts.
        //
        // mergeMap preserves neither order nor grouping, so results are keyed
        // back to their store by the report each one carries.
        return from$(STORES).pipe(
            mergeMap(
                (store) => this.syncStore(store, from, to),
                STORE_SYNC_CONCURRENCY,
            ),
            toArray(),
            map((results) => ({
                records: results.flatMap((result) => result.records),
                reports: results.map((result) => result.report),
            })),
        );
    }

    // Asks each platform how many orders a period actually contains, so the
    // page can compare that against what is stored and say whether the screen
    // is complete.
    //
    // This exists because a sync that dies halfway writes what it managed and
    // reports nothing: a January truncated to 79 of 654 Shopee orders renders
    // as a plausible quiet month. Nothing already in the system could tell the
    // difference — the stored rows are self-consistent, and basisGaps only
    // catches rows that exist but are filtered out, never rows that were never
    // written.
    //
    // Deliberately re-queries the platforms rather than reusing anything the
    // sync recorded. A count derived from the sync's own bookkeeping would
    // confirm the sync's own errors; the only trustworthy check comes from the
    // side that actually owns the data.
    //
    // Only index endpoints are called — no per-order detail — so verifying a
    // month costs a handful of requests rather than the hundreds a re-sync
    // would.
    verify(from: Moment, to: Moment): Observable<FinanceVerifyReport[]> {
        return from$(STORES).pipe(
            mergeMap(
                (store) => this.verifyStore(store, from, to),
                STORE_SYNC_CONCURRENCY,
            ),
            toArray(),
        );
    }

    private verifyStore(
        store: StoreConfig,
        from: Moment,
        to: Moment,
    ): Observable<FinanceVerifyReport> {
        const base: FinanceVerifyReport = {
            storeId: store.storeId,
            title: store.title,
            platformOrders: null,
            platformSettlement: null,
        };

        let probe: Observable<FinanceVerifyReport>;

        switch (store.platform) {
            case 'shopee': {
                // The escrow list is the index: one entry per settled order,
                // carrying the payout. Three calls for a month.
                const service = this.serviceFor(
                    store,
                    'ssmAuthInfo',
                    ShopeeOpenServiceV2,
                );
                probe = from$(splitWindows(from, to, SHOPEE_WINDOW_DAYS)).pipe(
                    mergeMap(
                        (window) =>
                            service.getEscrowList(window.from, window.to),
                        1,
                    ),
                    toArray(),
                    map((pages: any[][]) => {
                        const rows = pages.flat();
                        return {
                            ...base,
                            platformOrders: rows.length,
                            platformSettlement: round2(
                                rows.reduce(
                                    (sum, row) =>
                                        sum +
                                        (Number(_get(row, 'payout_amount')) ||
                                            0),
                                    0,
                                ),
                            ),
                        };
                    }),
                );
                break;
            }
            case 'tiktok': {
                // Statements carry the settled total directly. One call.
                // Order count is not available at this level, so only the
                // money is compared — which is the figure that matters.
                const service = this.serviceFor(
                    store,
                    'tiktokAuthInfo',
                    TiktokOpenService,
                );
                probe = service.getStatements(from.unix(), to.unix()).pipe(
                    map((statements: any[]) => {
                        // TikTok's statement_time_ge is not strict — asked for
                        // a window starting 30 Jun 17:00 UTC it returns
                        // statements dated 30 Jun 00:00 UTC. Trusting the
                        // API's own filter made 49 July orders look missing
                        // when they were correctly stored under June, and sent
                        // me hunting a 3,345 THB gap that did not exist.
                        // Filter on the value rather than on the request.
                        const inWindow = statements.filter((statement) => {
                            const at = Number(
                                _get(statement, 'statement_time'),
                            );
                            return (
                                Number.isFinite(at) &&
                                at >= from.unix() &&
                                at <= to.unix()
                            );
                        });
                        return {
                            ...base,
                            platformOrders: null,
                            platformSettlement: round2(
                                inWindow.reduce(
                                    (sum, statement) =>
                                        sum +
                                        (Number(
                                            _get(
                                                statement,
                                                'settlement_amount',
                                            ),
                                        ) || 0),
                                    0,
                                ),
                            ),
                        };
                    }),
                );
                break;
            }
            case 'lazada': {
                // Lazada has no per-order index — the ledger IS the index — so
                // this re-reads it and counts distinct order_no. Costs more
                // than the other two, but skips the order-date join and every
                // per-order call, so still a fraction of a sync.
                const service = this.serviceFor(
                    store,
                    'lazadaAuthInfo',
                    LazadaOpenService,
                );
                probe = service
                    .getFinanceTransactions(
                        from.format('YYYY-MM-DD'),
                        to.format('YYYY-MM-DD'),
                    )
                    .pipe(
                        map((lines: unknown[]) => {
                            const orders = new Set(
                                lines
                                    .map((line) =>
                                        String(_get(line, 'order_no') ?? ''),
                                    )
                                    .filter(Boolean),
                            );
                            return {
                                ...base,
                                platformOrders: orders.size,
                                platformSettlement: null,
                            };
                        }),
                    );
                break;
            }
        }

        // A probe that fails must say so, not report zero — zero would read as
        // "the platform confirms this month is empty", which is the exact
        // false reassurance this whole method exists to prevent.
        return probe.pipe(
            catchError((error) =>
                of({
                    ...base,
                    error: _get(error, 'message') || String(error),
                }),
            ),
        );
    }

    private syncStore(
        store: StoreConfig,
        from: Moment,
        to: Moment,
    ): Observable<{
        records: FinanceOrderRecord[];
        report: FinanceSyncStoreReport;
    }> {
        const base: FinanceSyncStoreReport = {
            storeId: store.storeId,
            title: store.title,
            records: 0,
            reconciliationFailures: 0,
            missingTaxPoint: 0,
            duplicateIds: 0,
        };

        let fetch: Observable<{
            records: FinanceOrderRecord[];
            report: FinanceSyncStoreReport;
        }>;

        switch (store.platform) {
            case 'shopee':
                fetch = this.syncShopee(store, from, to, base);
                break;
            case 'lazada':
                fetch = this.syncLazada(store, from, to, base);
                break;
            case 'tiktok':
                fetch = this.syncTiktok(store, from, to, base);
                break;
        }

        return fetch.pipe(
            map((result) => ({
                records: result.records,
                report: this.finalizeReport(result.records, result.report),
            })),
            catchError((error) =>
                of({
                    records: [],
                    report: {
                        ...base,
                        error: _get(error, 'message') || String(error),
                    },
                }),
            ),
        );
    }

    // --- Shopee ------------------------------------------------------------
    //
    // Three calls joined: escrow list gives which orders settled and when,
    // escrow detail gives the fees, order detail gives the dates. The third is
    // the existing getOrderDetails, whose create_time is the tax point — so
    // Shopee's tax point costs nothing extra.
    private syncShopee(
        store: ShopeeStoreConfig,
        from: Moment,
        to: Moment,
        base: FinanceSyncStoreReport,
    ): Observable<{
        records: FinanceOrderRecord[];
        report: FinanceSyncStoreReport;
    }> {
        const service = this.serviceFor(
            store,
            'ssmAuthInfo',
            ShopeeOpenServiceV2,
        );
        const windows = splitWindows(from, to, SHOPEE_WINDOW_DAYS);

        // Windows one at a time. A month is three of them and each paginates,
        // so running them together triples this store's share of the SSM
        // budget for no useful gain — the store is already the unit of
        // parallelism, and it is set to one.
        return from$(windows).pipe(
            mergeMap(
                (window) => service.getEscrowList(window.from, window.to),
                1,
            ),
            toArray(),
            mergeMap((pages: any[][]) => {
                const escrowRows = pages.flat();
                const orderSns: string[] = escrowRows
                    .map((row) => String(_get(row, 'order_sn') ?? ''))
                    .filter(Boolean);
                if (orderSns.length === 0) {
                    return of({ records: [], report: base });
                }
                const releaseBySn = new Map<string, number>(
                    escrowRows.map((row) => [
                        String(_get(row, 'order_sn')),
                        Number(_get(row, 'escrow_release_time')) || 0,
                    ]),
                );
                // getOrderDetails emits one array per 50-order chunk (the
                // order pages rely on that), and forkJoin keeps only the last
                // emission — so it is gathered here first. Without this the
                // join saw one chunk of orders, and every escrow row outside
                // it landed with no create_time and therefore no tax point.
                return forkJoin({
                    details: service.getEscrowDetails(orderSns),
                    orders: service.getOrderDetails(orderSns).pipe(
                        toArray(),
                        map((chunks: any[][]) => chunks.flat()),
                    ),
                }).pipe(
                    // Shopee can answer a get_order_detail chunk with an
                    // empty order_list and no error — one chunk of 50 did,
                    // on 2026-10-23 — and that chunk's orders then have no
                    // create_time. Ask once more for exactly the misses
                    // before giving up on them.
                    mergeMap(({ details, orders }) => {
                        const orderBySn = _keyBy(orders, 'order_sn');
                        const missing = details
                            .map((detail: unknown) =>
                                String(_get(detail, 'order_sn')),
                            )
                            .filter((sn: string) => sn && !orderBySn[sn]);
                        if (missing.length === 0) {
                            return of({ details, orderBySn });
                        }
                        console.warn(
                            `${store.storeId}: ${missing.length} of ${orderSns.length} order details missing after first pass; refetching`,
                        );
                        return service.getOrderDetails(missing).pipe(
                            toArray(),
                            map((chunks: any[][]) => ({
                                details,
                                orderBySn: {
                                    ...orderBySn,
                                    ..._keyBy(chunks.flat(), 'order_sn'),
                                },
                            })),
                        );
                    }),
                    map(({ details, orderBySn }) => {
                        const now = moment().unix();
                        let failures = 0;
                        let unjoined = 0;
                        const records: FinanceOrderRecord[] = [];
                        for (const escrowDetail of details) {
                            const sn = String(_get(escrowDetail, 'order_sn'));
                            // Not written. The upsert $sets every field, so
                            // a record built without its order detail would
                            // null a tax point an earlier sync had stored;
                            // leaving the stored row alone and saying so is
                            // the lesser harm. On a first sync the order is
                            // simply absent until the month is re-synced,
                            // and the verify check shows it as short.
                            if (!orderBySn[sn]) {
                                unjoined += 1;
                                continue;
                            }
                            const record = normalizeShopeeOrder(
                                {
                                    escrowDetail,
                                    escrowReleaseTime:
                                        releaseBySn.get(sn) ?? null,
                                    orderDetail: orderBySn[sn],
                                },
                                {
                                    storeId: store.storeId,
                                    entityId: store.entityId,
                                    fetchedAt: now,
                                    syncedAt: now,
                                },
                            );
                            if (!reconcileShopee(record).ok) {
                                failures += 1;
                            }
                            records.push(record);
                        }
                        return {
                            records,
                            report: {
                                ...base,
                                reconciliationFailures: failures,
                                unjoinedOrders: unjoined,
                            },
                        };
                    }),
                );
            }),
        );
    }

    // --- Lazada ------------------------------------------------------------
    private syncLazada(
        store: LazadaStoreConfig,
        from: Moment,
        to: Moment,
        base: FinanceSyncStoreReport,
    ): Observable<{
        records: FinanceOrderRecord[];
        report: FinanceSyncStoreReport;
    }> {
        const service = this.serviceFor(
            store,
            'lazadaAuthInfo',
            LazadaOpenService,
        );
        return forkJoin({
            lines: service.getFinanceTransactions(
                from.format('YYYY-MM-DD'),
                to.format('YYYY-MM-DD'),
            ),
            // The ledger has no order date and no voucher split, so both
            // come from /orders/get. Reached back by
            // LAZADA_ORDER_LOOKBACK_DAYS because an order settling in this
            // period was created before it — sometimes well before, on
            // slow-delivery or disputed orders.
            orderFacts: service.getOrderFacts(
                from
                    .clone()
                    .subtract(LAZADA_ORDER_LOOKBACK_DAYS, 'days')
                    .toISOString(),
                to.toISOString(),
            ),
        }).pipe(
            map(({ lines, orderFacts }) => {
                const now = moment().unix();
                const result = normalizeLazadaOrders(lines, {
                    storeId: store.storeId,
                    entityId: store.entityId,
                    orderFacts,
                    fetchedAt: now,
                    syncedAt: now,
                });
                return {
                    records: result.records,
                    report: {
                        ...base,
                        unmappedFeeNames: result.unmappedFeeNames,
                        unattributedTotal: result.unattributedTotal,
                        unparsedAmounts: result.unparsedAmounts,
                    },
                };
            }),
        );
    }

    // --- TikTok ------------------------------------------------------------
    private syncTiktok(
        store: TiktokStoreConfig,
        from: Moment,
        to: Moment,
        base: FinanceSyncStoreReport,
    ): Observable<{
        records: FinanceOrderRecord[];
        report: FinanceSyncStoreReport;
    }> {
        const service = this.serviceFor(
            store,
            'tiktokAuthInfo',
            TiktokOpenService,
        );
        return service.getStatements(from.unix(), to.unix()).pipe(
            mergeMap((statements: any[]) => {
                if (statements.length === 0) {
                    return of({ records: [], report: base });
                }
                // Bounded, not forkJoin. A month is ~36 statements and each
                // paginates, so mapping them into forkJoin opened 36
                // concurrent chains at once and earned TikTok's own
                // `36009002 Too many requests for downstream`. This is the
                // heaviest fan-out in the whole sync — Shopee and Lazada page
                // a handful of times, TikTok pages once per statement.
                return from$(statements)
                    .pipe(
                        mergeMap(
                            (statement) =>
                                service
                                    .getStatementTransactions(
                                        String(_get(statement, 'id')),
                                    )
                                    .pipe(
                                        map((rows: unknown[]) => ({
                                            statementTime:
                                                Number(
                                                    _get(
                                                        statement,
                                                        'statement_time',
                                                    ),
                                                ) || null,
                                            statementId: String(
                                                _get(statement, 'id') ?? '',
                                            ),
                                            rows,
                                        })),
                                    ),
                            FINANCE_FETCH_CONCURRENCY,
                        ),
                        toArray(),
                    )
                    .pipe(
                        map((groups) => {
                            const now = moment().unix();
                            let failures = 0;
                            const records: FinanceOrderRecord[] = [];
                            for (const group of groups) {
                                for (const row of group.rows) {
                                    const record = normalizeTiktokTransaction(
                                        row,
                                        {
                                            storeId: store.storeId,
                                            entityId: store.entityId,
                                            statementTime: group.statementTime,
                                            statementId:
                                                group.statementId || null,
                                            fetchedAt: now,
                                            syncedAt: now,
                                        },
                                    );
                                    if (!record) {
                                        continue;
                                    }
                                    if (!reconcileTiktok(row).ok) {
                                        failures += 1;
                                    }
                                    records.push(record);
                                }
                            }
                            return {
                                records,
                                report: {
                                    ...base,
                                    reconciliationFailures: failures,
                                },
                            };
                        }),
                    );
            }),
        );
    }

    // Applies the VAT split, which the normalizers deliberately leave null
    // because they have no opinion about registration.
    //
    // Keyed on the TAX POINT, not on today: an entity registered now was not
    // registered for last year's sales, and regenerating an old period must not
    // retroactively add VAT to it. A record with no tax point gets no split at
    // all rather than a guessed one — which is why missingTaxPoint is counted
    // and surfaced instead of being quietly tolerated.
    private finalizeReport(
        records: FinanceOrderRecord[],
        report: FinanceSyncStoreReport,
    ): FinanceSyncStoreReport {
        let missingTaxPoint = 0;
        const seenIds = new Set<string>();
        let duplicateIds = 0;
        for (const record of records) {
            if (seenIds.has(record._id)) {
                duplicateIds += 1;
            } else {
                seenIds.add(record._id);
            }
            if (record.taxPointDate === null) {
                missingTaxPoint += 1;
                continue;
            }
            const taxPointIso = moment
                .unix(record.taxPointDate)
                .format('YYYY-MM-DD');
            if (!isVatRegisteredAt(record.entityId, taxPointIso)) {
                // Input tax is claimable only by a registrant. Lazada states
                // VAT on every seller's fees, registered or not, and the
                // normalizer carries it; for pensri it is a cost, not a
                // claim, and the same nullability rule as `vat` applies.
                record.feeVat = null;
                continue;
            }
            // Split at the tax point's rate, like the issued documents —
            // an undated split re-rates historical records the day a second
            // entry lands in VAT_RATES.
            // From the taxable consideration, not buyerTotal: the buyer's
            // payment is already net of platform-funded vouchers and coins,
            // which stay in the base.
            record.vat = splitVatInclusive(
                record.gross.taxableConsideration,
                taxPointIso,
            );
            // Claimable input tax on the platform's fees. Lazada states it
            // per line, so a feeVat set by the normalizer is authoritative
            // and left alone. Shopee and TikTok state nothing, so it is
            // ESTIMATED as 7/107 of the buckets the platform itself invoices
            // with VAT — not of the whole fee total, which also holds
            // domestic shipping (VAT-exempt transport, and on TikTok billed
            // by Thai Happy Logistic) and TikTok creators' affiliate
            // commissions. `base` is the full fee total so base + input still
            // describes the whole charge. The claim itself needs the
            // platform's tax invoice in the taxpayer's name (assessment §2.3).
            if (record.feeVat === null && record.fees.total !== 0) {
                const bearing = vatBearingFeeTotal(record);
                const split = splitVatInclusive(bearing, taxPointIso);
                record.feeVat = {
                    base: round2(record.fees.total - split.output),
                    input: round2(split.output),
                };
            }
        }
        return {
            ...report,
            records: records.length,
            missingTaxPoint,
            duplicateIds,
        };
    }

    // Each store needs its own injector because the platform services read
    // their credentials from a DI token rather than an argument — see
    // store-scoped.ts.
    private serviceFor<T>(
        store: StoreConfig,
        authInfoToken: string,
        serviceClass: new (...args: any[]) => T,
    ): T {
        return storeService(this.injector, store, authInfoToken, serviceClass);
    }
}
